import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Divider,
  Chip,
  Grid,
  Paper,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ArrowBackIos as PrevIcon,
  ArrowForwardIos as NextArrowIcon,
  Favorite as LikeIcon,
  ChatBubble as CommentIcon,
  AutoStories as StoryIcon,
  Instagram as InstagramIcon,
  Check as DoneIcon,
  SkipNext as SkipIcon,
} from '@mui/icons-material';
import { leadApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, NicheChip, EmptyState } from '../components/common';
import {
  formatInstagramHandle,
  formatNumber,
  getInstagramProfileUrl,
} from '../utils/helpers';

const WarmUpMode = () => {
  const navigate = useNavigate();
  const { showSnackbar, fetchDashboardData } = useApp();

  const [leads, setLeads] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWarmUpLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWarmUpLeads = async () => {
    try {
      const response = await leadApi.getAll({
        stage: 'warming,new',
        limit: 50,
        sortBy: 'likesGiven',
        sortOrder: 'asc',
      });
      setLeads(response.data.leads);
    } catch (error) {
      showSnackbar('Failed to load leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const currentLead = leads[currentIndex];

  const handleWarmup = async (type) => {
    if (!currentLead) return;

    try {
      await leadApi.incrementWarmup(currentLead._id, type);

      // Update local state
      setLeads(prev =>
        prev.map(lead =>
          lead._id === currentLead._id
            ? {
                ...lead,
                likesGiven: type === 'like' ? (lead.likesGiven || 0) + 1 : lead.likesGiven,
                commentsGiven: type === 'comment' ? (lead.commentsGiven || 0) + 1 : lead.commentsGiven,
                storyReplies: type === 'story_reply' ? (lead.storyReplies || 0) + 1 : lead.storyReplies,
                stage: lead.stage === 'new' ? 'warming' : lead.stage,
              }
            : lead
        )
      );

      showSnackbar(`${type.replace('_', ' ')} logged!`, 'success');
    } catch (error) {
      showSnackbar('Failed to log activity', 'error');
    }
  };

  const handleDoneWarming = async () => {
    if (!currentLead) return;

    try {
      await leadApi.update(currentLead._id, { stage: 'dm_sent' });
      setLeads(prev => prev.filter(lead => lead._id !== currentLead._id));
      showSnackbar('Moved to DM stage!', 'success');
      fetchDashboardData();

      // Move to next lead or start from 0
      if (currentIndex >= leads.length - 1) {
        setCurrentIndex(0);
      }
    } catch (error) {
      showSnackbar('Failed to update lead', 'error');
    }
  };

  const handleSkip = () => {
    if (currentIndex < leads.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(leads.length - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < leads.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading warm-up queue..." />;
  }

  if (leads.length === 0) {
    return (
      <EmptyState
        title="No leads to warm up"
        description="Add some leads in the 'new' or 'warming' stage to start warming up"
        actionLabel="View All Leads"
        onAction={() => navigate('/leads')}
      />
    );
  }

  const warmupProgress = currentLead
    ? (((currentLead.likesGiven || 0) + (currentLead.commentsGiven || 0) + (currentLead.storyReplies || 0)) / 5) * 100
    : 0;

  const isReadyToDM = warmupProgress >= 100;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700} color="warning.main">
            Warm Up Mode
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Engage with leads before sending your first DM
          </Typography>
        </Box>
        <Chip
          label={`${currentIndex + 1} of ${leads.length}`}
          variant="outlined"
        />
        <Button
          variant="outlined"
          onClick={() => navigate('/leads?stage=warming')}
        >
          Exit
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Main Card */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ bgcolor: 'warning.light' }}>
            <CardContent>
              {/* Lead Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700}>
                    {formatInstagramHandle(currentLead.instagramHandle)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {currentLead.businessName || 'No business name'} • {currentLead.city}, {currentLead.stateProvince}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <NicheChip niche={currentLead.niche} />
                    <Chip label={`${formatNumber(currentLead.followerCount)} followers`} size="small" />
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<InstagramIcon />}
                  onClick={() => window.open(getInstagramProfileUrl(currentLead.instagramHandle), '_blank')}
                  sx={{ bgcolor: '#E4405F', '&:hover': { bgcolor: '#D13752' } }}
                >
                  Open Profile
                </Button>
              </Box>

              {/* Progress */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Warm-up Progress
                  </Typography>
                  <Typography variant="body2">
                    {currentLead.likesGiven || 0} likes, {currentLead.commentsGiven || 0} comments, {currentLead.storyReplies || 0} story replies
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(warmupProgress, 100)}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    bgcolor: 'warning.dark',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: isReadyToDM ? 'success.main' : 'white',
                      borderRadius: 6,
                    },
                  }}
                />
                {isReadyToDM && (
                  <Typography variant="body2" color="success.dark" fontWeight={600} sx={{ mt: 1 }}>
                    Ready to send DM!
                  </Typography>
                )}
              </Box>

              {/* Quick Log Buttons */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Quick Log:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<LikeIcon />}
                  onClick={() => handleWarmup('like')}
                  sx={{ bgcolor: 'white', color: 'error.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Liked Post ({currentLead.likesGiven || 0})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CommentIcon />}
                  onClick={() => handleWarmup('comment')}
                  sx={{ bgcolor: 'white', color: 'info.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Commented ({currentLead.commentsGiven || 0})
                </Button>
                <Button
                  variant="contained"
                  startIcon={<StoryIcon />}
                  onClick={() => handleWarmup('story_reply')}
                  sx={{ bgcolor: 'white', color: 'secondary.main', '&:hover': { bgcolor: 'grey.100' } }}
                >
                  Story Reply ({currentLead.storyReplies || 0})
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  startIcon={<SkipIcon />}
                  onClick={handleSkip}
                  sx={{ borderColor: 'warning.dark', color: 'warning.dark' }}
                >
                  Skip
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DoneIcon />}
                  onClick={handleDoneWarming}
                  disabled={!isReadyToDM}
                >
                  Done Warming - Send DM
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <IconButton onClick={handlePrev} size="large">
              <PrevIcon />
            </IconButton>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
              {currentIndex + 1} / {leads.length}
            </Typography>
            <IconButton onClick={handleNext} size="large">
              <NextArrowIcon />
            </IconButton>
          </Box>
        </Grid>

        {/* Sidebar - Queue */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Warm-up Queue
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
                {leads.map((lead, index) => {
                  const progress = ((lead.likesGiven || 0) + (lead.commentsGiven || 0) + (lead.storyReplies || 0)) / 5 * 100;
                  const isActive = index === currentIndex;

                  return (
                    <Paper
                      key={lead._id}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        bgcolor: isActive ? 'warning.light' : 'transparent',
                        border: '1px solid',
                        borderColor: isActive ? 'warning.main' : 'transparent',
                        '&:hover': { bgcolor: isActive ? 'warning.light' : 'grey.50' },
                      }}
                      onClick={() => setCurrentIndex(index)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {formatInstagramHandle(lead.instagramHandle)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {lead.businessName}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" color={progress >= 100 ? 'success.main' : 'text.secondary'}>
                            {Math.min(progress, 100).toFixed(0)}%
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(progress, 100)}
                            color={progress >= 100 ? 'success' : 'warning'}
                            sx={{ width: 60, height: 4, borderRadius: 2, mt: 0.5 }}
                          />
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WarmUpMode;
